import { useState } from 'react';
import {
  X,
  Package,
  Snowflake,
  Thermometer,
  Box,
  Sun,
  Truck,
  FileText,
  Link2,
  ChefHat,
  Warehouse,
  ShoppingCart,
  ArrowRightLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type { CatalogItem } from '../../types';
import { STORAGE_TYPE_LABELS } from '../../types';
import { OverviewTab } from './tabs/OverviewTab';
import { SuppliersTab } from './tabs/SuppliersTab';
import { RecipesTab } from './tabs/RecipesTab';
import { InventoryTab } from './tabs/InventoryTab';
import { PurchasesTab } from './tabs/PurchasesTab';
import { RelatedTab } from './tabs/RelatedTab';
import { AttachmentsTab } from './tabs/AttachmentsTab';

type TabId = 'overview' | 'suppliers' | 'recipes' | 'inventory' | 'purchases' | 'related' | 'attachments';

interface Tab {
  id: TabId;
  label: string;
  icon: typeof Package;
}

const TABS: Tab[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Package 
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    icon: Truck 
  },
  {
    id: 'recipes',
    label: 'Recipes',
    icon: ChefHat 
  },
  {
    id: 'inventory',
    label: 'Stock',
    icon: Warehouse 
  },
  {
    id: 'purchases',
    label: 'Purchases',
    icon: ShoppingCart 
  },
  {
    id: 'related',
    label: 'Related',
    icon: Link2 
  },
  {
    id: 'attachments',
    label: 'Files',
    icon: FileText 
  },
];

const STORAGE_TYPE_ICONS = {
  frozen: Snowflake,
  chilled: Thermometer,
  dry: Box,
  ambient: Sun,
};

const STORAGE_TYPE_COLORS = {
  frozen: 'text-blue-500 bg-blue-50',
  chilled: 'text-cyan-500 bg-cyan-50',
  dry: 'text-amber-500 bg-amber-50',
  ambient: 'text-orange-500 bg-orange-50',
};

interface ProductDetailModalProps {
  readonly item: CatalogItem;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEdit?: () => void;
  readonly onMerge?: (targetItemId: string) => void;
  readonly onAddSubstitute?: () => void;
  readonly onAddRelatedItem?: () => void;
  readonly onUploadFile?: () => void;
}

export function ProductDetailModal({
  item,
  isOpen,
  onClose,
  onEdit,
  onMerge,
  onAddSubstitute,
  onAddRelatedItem,
  onUploadFile,
}: ProductDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  if (!isOpen) return null;

  const StorageIcon = STORAGE_TYPE_ICONS[item.storageType];
  const storageColor = STORAGE_TYPE_COLORS[item.storageType];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex h-[90vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-xl">
        <ModalHeader
          item={item}
          storageIcon={StorageIcon}
          storageColor={storageColor}
          onClose={onClose}
          onEdit={onEdit}
          onAddSubstitute={onAddSubstitute}
        />

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="flex-1 overflow-y-auto p-6">
          <TabContent
            activeTab={activeTab}
            item={item}
            onMerge={onMerge}
            onAddRelatedItem={onAddRelatedItem}
            onUploadFile={onUploadFile}
          />
        </div>
      </div>
    </div>
  );
}

interface ModalHeaderProps {
  readonly item: CatalogItem;
  readonly storageIcon: typeof Package;
  readonly storageColor: string;
  readonly onClose: () => void;
  readonly onEdit?: () => void;
  readonly onAddSubstitute?: () => void;
}

function ModalHeader({
  item,
  storageIcon: storageIconProp,
  storageColor,
  onClose,
  onEdit,
  onAddSubstitute,
}: ModalHeaderProps) {
  const StorageIcon = storageIconProp;
  
  return (
    <div className="flex items-start justify-between border-b p-6">
      <div className="flex items-start gap-4">
        <div className={cn('rounded-lg p-3', storageColor)}>
          <StorageIcon className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{item.name}</h2>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
            <span>SKU: {item.sku}</span>
            {item.internalCode && (
              <>
                <span>•</span>
                <span>Internal: {item.internalCode}</span>
              </>
            )}
            <span>•</span>
            <span>{STORAGE_TYPE_LABELS[item.storageType]}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
        )}
        {onAddSubstitute && (
          <Button variant="outline" size="sm" onClick={onAddSubstitute}>
            <ArrowRightLeft className="mr-1 h-4 w-4" />
            Add Substitute
          </Button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

interface TabNavigationProps {
  readonly activeTab: TabId;
  readonly onTabChange: (tab: TabId) => void;
}

function TabNavigation({
  activeTab, onTabChange 
}: TabNavigationProps) {
  return (
    <div className="border-b px-6">
      <nav className="flex gap-1">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <TabIcon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

interface TabContentProps {
  readonly activeTab: TabId;
  readonly item: CatalogItem;
  readonly onMerge?: (targetItemId: string) => void;
  readonly onAddRelatedItem?: () => void;
  readonly onUploadFile?: () => void;
}

function TabContent({
  activeTab, item, onMerge, onAddRelatedItem, onUploadFile 
}: TabContentProps) {
  switch (activeTab) {
    case 'overview':
      return <OverviewTab item={item} />;
    case 'suppliers':
      return <SuppliersTab suppliers={item.suppliers} costHistory={item.costHistory} />;
    case 'recipes':
      return <RecipesTab recipeUsage={item.recipeUsage} />;
    case 'inventory':
      return <InventoryTab stockLevels={item.stockLevels} />;
    case 'purchases':
      return <PurchasesTab recentPurchases={item.recentPurchases} />;
    case 'related':
      return <RelatedTab relatedItems={item.relatedItems} onMerge={onMerge} onAddRelatedItem={onAddRelatedItem} />;
    case 'attachments':
      return <AttachmentsTab attachments={item.attachments} onUploadFile={onUploadFile} />;
    default:
      return null;
  }
}
